import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { articles, formatLastUpdated } from '../data/articles';
import { getToolBySlug, tools } from '../data/tools';
import Carousel from '../components/Carousel';

interface Heading {
  id: string;
  text: string;
  level: number;
}

// Build sorted tool list once (longest name first to avoid partial matches)
const sortedTools = [...tools].sort((a, b) => b.name.length - a.name.length);
const escapeRegex = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
const toolNamePattern = sortedTools.map(t => escapeRegex(t.name)).join('|');
const TOOL_RE = new RegExp(`(?<!\\[)(?<!\\[.*)(${toolNamePattern})(?![^\\[]*\\])(?!\\()`, 'g');

// Pre-process markdown: replace bare tool names with markdown links
// Skips names already inside []() markdown links or code blocks
const injectToolLinks = (markdown: string): string => {
  const lines = markdown.split('\n');
  return lines.map(line => {
    // Don't touch code blocks, headings, or lines that are already links
    if (line.startsWith('```') || line.startsWith('    ') || line.startsWith('#')) return line;
    return line.replace(TOOL_RE, (match) => {
      const tool = sortedTools.find(t => t.name === match);
      if (!tool) return match;
      return `[${match}](${tool.website})`;
    });
  }).join('\n');
};

const ArticleViewPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navBack = useNavigate();
  const [content, setContent] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [headings, setHeadings] = useState<Heading[]>([]);
  const [activeId, setActiveId] = useState<string>('');
  const [tocTop, setTocTop] = useState<number>(120);
  const articleRef = React.useRef<HTMLElement>(null);
  const wrapperRef = React.useRef<HTMLDivElement>(null);

  const article = articles.find(a => a.slug === slug);

  // Extract headings from markdown
  useEffect(() => {
    if (!content) return;
    const headingRegex = /^(#{1,3})\s+(.+)$/gm;
    const extractedHeadings: Heading[] = [];
    let match;
    while ((match = headingRegex.exec(content)) !== null) {
      const level = match[1].length;
      const text = match[2];
      const id = text.toLowerCase().replace(/[^a-z0-9]+/g, '-');
      extractedHeadings.push({ id, text, level });
    }
    setHeadings(extractedHeadings);
  }, [content]);

  // Track active section and TOC position on scroll
  useEffect(() => {
    if (headings.length === 0) return;
    const handleScroll = () => {
      const scrollPosition = window.scrollY + 150;
      let currentId = headings[0]?.id || '';
      for (const heading of headings) {
        const element = document.getElementById(heading.id);
        if (element) {
          if (scrollPosition >= element.offsetTop) currentId = heading.id;
          else break;
        }
      }
      setActiveId(currentId);
      if (articleRef.current && wrapperRef.current) {
        const wrapperTop = wrapperRef.current.offsetTop;
        const articleHeight = articleRef.current.offsetHeight;
        const scrollY = window.scrollY;
        const maxTop = wrapperTop + articleHeight - 400;
        setTocTop(scrollY + 120 > maxTop ? maxTop - scrollY : 120);
      }
    };
    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
    };
  }, [headings]);

  useEffect(() => {
    if (!article) { setError('Article not found'); setLoading(false); return; }
    fetch(`/articles/${article.slug}.md`)
      .then(r => { if (!r.ok) throw new Error('Article content not found'); return r.text(); })
      .then(md => { setContent(md); setLoading(false); })
      .catch(err => { setError(err.message); setLoading(false); });
  }, [article]);

  const scrollToHeading = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      window.scrollTo({ top: element.getBoundingClientRect().top + window.pageYOffset - 100, behavior: 'smooth' });
    }
  };

  if (loading) return (
    <div style={{ padding: '4rem 0', textAlign: 'center' }}>
      <div className="container"><p>Loading article...</p></div>
    </div>
  );

  if (error || !article) return (
    <div style={{ padding: '4rem 0', textAlign: 'center' }}>
      <div className="container">
        <h1>News Not Found</h1>
        <p>The news article you're looking for doesn't exist.</p>
        <Link to="/articles" className="button button-primary">Back to ChooseNews</Link>
      </div>
    </div>
  );

  const processedContent = injectToolLinks(content);

  return (
    <>
      <Helmet>
        <title>{article.title} | ChooseAI</title>
        <meta name="description" content={article.excerpt} />
        <meta property="og:title" content={`${article.title} | ChooseAI`} />
        <meta property="og:description" content={article.excerpt} />
        <meta property="og:url" content={`https://chooseai.co.uk/articles/${article.slug}`} />
        <meta property="og:type" content="article" />
        <meta property="article:modified_time" content={article.lastUpdated} />
        <link rel="canonical" href={`https://chooseai.co.uk/articles/${article.slug}`} />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Article",
          "headline": article.title,
          "description": article.excerpt,
          "dateModified": article.lastUpdated,
          "author": { "@type": "Organization", "name": article.author, "url": "https://chooseai.co.uk" },
          "publisher": { "@type": "Organization", "name": "ChooseAI", "url": "https://chooseai.co.uk" },
          "mainEntityOfPage": { "@type": "WebPage", "@id": `https://chooseai.co.uk/articles/${article.slug}` }
        })}</script>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://chooseai.co.uk/" },
            { "@type": "ListItem", "position": 2, "name": "ChooseNews", "item": "https://chooseai.co.uk/" },
            { "@type": "ListItem", "position": 3, "name": article.title, "item": `https://chooseai.co.uk/articles/${article.slug}` }
          ]
        })}</script>
      </Helmet>
      <div style={{ padding: '2rem 0' }}>
      <div className="container">
        {/* Breadcrumb */}
        <div style={{ marginBottom: 'var(--space-lg)' }}>
          <button onClick={() => navBack(-1)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-muted)', fontSize: '0.9rem', padding: 0, fontFamily: 'var(--font-primary)' }}>← Back</button>
        </div>

        {/* Article Header */}
        <header style={{ marginBottom: '3rem', textAlign: 'center' }}>
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 'var(--space-md)', marginBottom: 'var(--space-lg)' }}>
            <span className="badge badge-primary">{article.category}</span>
            <span className="text-muted">{article.readTime}</span>
          </div>
          <h1 style={{ fontSize: '2.5rem', marginBottom: 'var(--space-lg)', maxWidth: '800px', margin: '0 auto var(--space-lg) auto' }}>
            {article.title}
          </h1>
          <p style={{ fontSize: '1.2rem', color: 'var(--color-text-secondary)', marginBottom: 'var(--space-lg)', maxWidth: '600px', margin: '0 auto var(--space-lg) auto' }}>
            {article.excerpt}
          </p>
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 'var(--space-md)', fontSize: '0.9rem', color: 'var(--color-text-muted)' }}>
            <span>{formatLastUpdated(article.lastUpdated)}</span>
          </div>
        </header>

        {/* Article Content with TOC */}
        <div ref={wrapperRef} className="article-content-wrapper" style={{ position: 'relative' }}>
          {/* Table of Contents */}
          {headings.length > 0 && (
            <nav className="article-toc" style={{ position: 'fixed', top: `${tocTop}px` }}>
              <h4 style={{ fontSize: '0.85rem', fontWeight: '700', marginBottom: 'var(--space-md)', color: 'var(--color-text-primary)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Contents
              </h4>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {headings.map((heading) => (
                  <li key={heading.id} style={{ marginBottom: 'var(--space-xs)' }}>
                    <button
                      onClick={() => scrollToHeading(heading.id)}
                      className={`toc-link ${activeId === heading.id ? 'toc-link-active' : ''}`}
                      style={{ paddingLeft: `${(heading.level - 1) * 12 + 8}px` }}
                    >
                      {heading.text}
                    </button>
                  </li>
                ))}
              </ul>
            </nav>
          )}

          {/* Article Content */}
          <article
            ref={articleRef}
            className="markdown-content"
            style={{ maxWidth: '800px', margin: '0 auto', lineHeight: '1.8', fontSize: '1.1rem' }}
          >
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                h1: ({ node, ...props }) => {
                  const text = props.children?.toString() || '';
                  const id = text.toLowerCase().replace(/[^a-z0-9]+/g, '-');
                  return <h1 id={id} {...props} />;
                },
                h2: ({ node, ...props }) => {
                  const text = props.children?.toString() || '';
                  const id = text.toLowerCase().replace(/[^a-z0-9]+/g, '-');
                  return <h2 id={id} {...props} />;
                },
                h3: ({ node, ...props }) => {
                  const text = props.children?.toString() || '';
                  const id = text.toLowerCase().replace(/[^a-z0-9]+/g, '-');
                  return <h3 id={id} {...props} />;
                },
                // All auto-injected tool links are external
                a: ({ node, href, children, ...props }) => (
                  <a href={href} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--color-primary)', textDecoration: 'underline', fontWeight: '600' }}>{children}</a>
                ),
              }}
            >
              {processedContent}
            </ReactMarkdown>
          </article>
        </div>

        {/* Tools Mentioned Section */}
        {article.toolsMentioned.length > 0 && (
          <div style={{ marginTop: '4rem', maxWidth: '800px', margin: '4rem auto 0' }}>
            <h3 style={{ marginBottom: 'var(--space-lg)' }}>Tools Mentioned in This Article</h3>
            <div className="grid grid-3">
              {article.toolsMentioned.map(toolSlug => {
                const tool = getToolBySlug(toolSlug);
                if (!tool) return null;
                return (
                  <div key={tool.id} style={{ position: 'relative' }}>
                    <Link to={`/tools/${tool.slug}`} style={{ textDecoration: 'none', color: 'inherit' }}>
                      <div className="card" style={{ cursor: 'pointer', transition: 'transform var(--transition-speed), box-shadow var(--transition-speed)', textAlign: 'center', height: '100%', display: 'flex', flexDirection: 'column' }}>
                        <img
                          src={`/logos/${tool.slug}.png`}
                          alt={`${tool.name} logo`}
                          style={{ width: '48px', height: '48px', borderRadius: '8px', objectFit: 'contain', backgroundColor: '#FDF8F0', padding: '4px', margin: '0 auto var(--space-md) auto' }}
                          onError={(e) => { e.currentTarget.style.display = 'none'; }}
                        />
                        <h4 style={{ marginBottom: 'var(--space-sm)' }}>{tool.name}</h4>
                        <p style={{ fontSize: '0.9rem', marginBottom: 'var(--space-md)', flex: 1 }}>{tool.oneLiner}</p>
                        <a
                          href={tool.website}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          style={{ fontSize: '0.8rem', color: 'var(--color-primary)', fontWeight: '600', textDecoration: 'none' }}
                        >
                          Visit Website →
                        </a>
                      </div>
                    </Link>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Related News */}
        <div style={{ marginTop: '4rem', margin: '4rem auto 0' }}>
          <h3 style={{ marginBottom: 'var(--space-lg)', textAlign: 'center' }}>More News</h3>
          <Carousel
            items={articles
              .filter(a => a.id !== article.id)
              .sort((a, b) => new Date(b.lastUpdated).getTime() - new Date(a.lastUpdated).getTime())
              .slice(0, 3)
              .map((relatedArticle) => ({
                preview: { title: relatedArticle.title, subtitle: relatedArticle.readTime },
                content: (
                  <Link to={`/articles/${relatedArticle.slug}`} style={{ textDecoration: 'none', color: 'inherit', height: '100%', display: 'flex' }}>
                    <div className="card" style={{ cursor: 'pointer', transition: 'transform var(--transition-speed), box-shadow var(--transition-speed)', height: '100%', width: '100%', display: 'flex', flexDirection: 'column' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)', marginBottom: 'var(--space-md)' }}>
                        <span className="badge badge-primary">{relatedArticle.category}</span>
                        <span className="text-muted" style={{ fontSize: '0.9rem' }}>{relatedArticle.readTime}</span>
                      </div>
                      <h4 style={{ marginBottom: 'var(--space-md)' }}>{relatedArticle.title}</h4>
                      <p style={{ marginBottom: 'var(--space-lg)', flex: 1 }}>{relatedArticle.excerpt}</p>
                      <div style={{ textAlign: 'right' }}>
                        <span style={{ fontSize: '0.9rem', color: 'var(--color-primary)', fontWeight: '600' }}>Read Article →</span>
                      </div>
                    </div>
                  </Link>
                ),
              }))}
          />
          <div style={{ textAlign: 'center', marginTop: 'var(--space-lg)' }}>
            <Link to="/articles" className="button button-secondary">View All News</Link>
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default ArticleViewPage;
